import StepSection from './StepSection';
import { useClock } from '@/hooks/useClock';

interface CheckRow {
  index: string;
  name: string;
  detail: string;
  pass: boolean;
}

const checks: CheckRow[] = [
  { index: '01', name: 'Trend Alignment', detail: 'Bias agrees across 1H and 15M', pass: true },
  { index: '02', name: 'Market Structure', detail: 'Higher low confirmed at support', pass: true },
  { index: '03', name: 'Volatility & Spread', detail: 'Spread 0.18 · inside threshold', pass: true },
  { index: '04', name: 'Risk Gate', detail: 'Size versus exposure cap', pass: false },
];

const passedCount = checks.filter((item) => item.pass).length;

function StepEvaluate() {
  const clock = useClock();
  return (
    <StepSection
      id="step-02"
      index="02"
      stage="filter"
      title="Evaluate The Conditions"
      subtitle="A market move alone does not create a trade."
      body="The EA evaluates whether the market meets the predefined conditions required by its trading framework. If the required conditions are not present, the filter returns a reject and no trade is opened."
      reverse
      band
    >
      <div className="rounded-lg border border-background-300 bg-background-50 overflow-hidden">
        <div className="flex items-center justify-between px-4 md:px-5 h-11 border-b border-background-200 bg-background-100">
          <span className="flex items-center gap-2 text-[11px] font-data tracking-widest text-foreground-700">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-500 breathe"></span>
            FILTER ENGINE · EVALUATING
          </span>
          <span className="text-[10px] font-data tracking-widest text-foreground-400">
            RULE SET · {checks.length} CHECKS
          </span>
        </div>

        <div className="flex items-center justify-between border-b border-background-200 px-4 md:px-5 py-3.5">
          <div>
            <p className="text-[10px] font-data tracking-[0.22em] text-foreground-400">
              CANDIDATE SETUP
            </p>
            <p className="mt-1 font-heading font-bold text-foreground-950 text-[14px]">
              XAUUSD · BUY BIAS · LONDON
            </p>
          </div>
          <span className="font-data text-[11px] tracking-widest text-foreground-500">{clock}</span>
        </div>

        <div className="divide-y divide-background-200">
          {checks.map((item) => (
            <div key={item.index} className="flex items-center gap-3 px-4 md:px-5 py-3.5">
              <span className="w-5 shrink-0 font-data text-[11px] text-foreground-400">
                {item.index}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-foreground-900 truncate">
                  {item.name}
                </p>
                <p className="text-[11px] text-foreground-500 truncate">{item.detail}</p>
              </div>
              <span
                className={`inline-flex shrink-0 items-center gap-1.5 font-data text-[11px] tracking-wider ${
                  item.pass ? 'text-profit-600' : 'text-loss-600'
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    item.pass ? 'bg-profit-500' : 'bg-loss-500'
                  }`}
                ></span>
                {item.pass ? 'PASS' : 'FAIL'}
              </span>
            </div>
          ))}
        </div>

        <div className="border-t border-background-200 bg-background-100 px-4 md:px-5 py-4">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
            <span className="text-[10px] font-data tracking-[0.22em] text-foreground-400">
              FILTER RESULT · {passedCount}/{checks.length} PASSED
            </span>
            <div className="flex items-center gap-2.5">
              <span className="flex items-center gap-2 font-heading font-extrabold text-[18px] leading-none text-foreground-950">
                <i className="ri-close-circle-line text-loss-600 text-[16px]"></i>
                REJECT
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-primary-300 bg-primary-50 px-2.5 py-1">
                <i className="ri-loop-left-line text-[12px] text-primary-600"></i>
                <span className="whitespace-nowrap font-data text-[10px] tracking-wider text-primary-700">
                  01 · INGEST
                </span>
              </span>
            </div>
          </div>
          <p className="mt-3 border-t border-background-200 pt-3 text-[11px] leading-relaxed text-foreground-500">
            Every condition is required. A single unmet check stops the setup before it is ever sent
            to execution.
          </p>
        </div>
      </div>
    </StepSection>
  );
}

export default StepEvaluate;